import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Alert, Button, Card, Field } from "../../components/ui";

/**
 * Microsoft (Entra ID) is the normal way in: the Supabase project is
 * configured single-tenant, so only Viewpoint Microsoft 365 accounts can
 * complete it — and authenticating grants nothing by itself. Access comes
 * from an approved profile and an assigned role, decided in the database.
 *
 * The email/password form exists only in local development builds, for the
 * synthetic regression-test accounts. It is compiled out of the deployed
 * bundle, so the test accounts are never presented as a login route.
 */
export default function SignIn() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A failed OAuth round trip lands back here with error_description in
  // the URL. Surface it once, then clean the address bar.
  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search || window.location.hash.replace(/^#/, "?"),
    );
    const description = params.get("error_description") ?? params.get("error");
    if (description) {
      setError(description.replace(/\+/g, " "));
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  async function signInWithMicrosoft() {
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        // Authentication and basic identity only — no Graph data scopes.
        scopes: "openid profile email",
        redirectTo: window.location.origin,
      },
    });
    // On success the browser navigates away; only a failure to even start
    // the flow lands here.
    if (error) {
      setError(error.message);
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "100dvh", padding: 16 }}>
      <div style={{ width: "min(400px, 100%)" }}>
        <div className="brand" style={{ justifyContent: "center", fontSize: "1.15rem" }}>
          <img className="brand-mark" src="/icon-48.png" alt="" aria-hidden="true" />
          Viewpoint Core
        </div>

        <Card>
          <h1 style={{ fontSize: "1.15rem", marginBottom: 4 }}>Sign in</h1>
          <p className="muted small" style={{ marginBottom: 14 }}>
            Vehicle Management — development environment.
          </p>

          {error && <Alert tone="bad">{error}</Alert>}

          <Button
            type="button"
            variant="primary"
            block
            disabled={busy}
            onClick={() => void signInWithMicrosoft()}
          >
            {busy ? "Redirecting…" : "Sign in with Microsoft"}
          </Button>
          <p className="hint" style={{ marginTop: 10 }}>
            Use your Viewpoint Microsoft 365 account.
          </p>

          {import.meta.env.DEV && <DevPasswordForm onError={setError} />}
        </Card>

        <p className="hint" style={{ textAlign: "center", marginTop: 12 }}>
          Access is decided by the database. What you can see and change is governed by
          your role, not by this screen.
        </p>
      </div>
    </div>
  );
}

/** Local-development only: signs in the synthetic regression accounts. */
function DevPasswordForm({ onError }: { onError: (e: string | null) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    onError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) onError(error.message);
    setBusy(false);
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 18, borderTop: "1px solid var(--border, #ddd)", paddingTop: 14 }}>
      <p className="muted small" style={{ marginBottom: 10 }}>
        Development test accounts (local builds only)
      </p>
      <Field label="Email">
        <input
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field label="Password">
        <input
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Button type="submit" block disabled={busy || !email || !password}>
        {busy ? "Signing in…" : "Sign in with password"}
      </Button>
    </form>
  );
}
